/**
 * GlobalSearch — input + instant dropdown.
 *
 * Replaces NavbarSearch's context-filter behavior with a global finder.
 * The collapsed icon → expanded input pattern is preserved so the header
 * layout stays identical. Results are rendered in a floating panel below.
 *
 * Panel positioning: rendered via createPortal to document.body with
 * position:fixed so it escapes the overflow:hidden header wrapper in
 * AppShell.tsx. Coordinates are computed from containerRef.getBoundingClientRect()
 * and recomputed on open, scroll, and resize to stay anchored to the input.
 *
 * Phase 3 additions:
 * - ⌘K / Ctrl+K global hotkey to open & focus.
 * - ArrowUp / ArrowDown / Enter keyboard navigation through result rows.
 * - aria-activedescendant + role="option" on rows for screen-reader selection.
 * - Loading skeleton, per-branch empty state, and error state (all i18n).
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useGlobalSearchQuery } from "./useGlobalSearchQuery";
import { GlobalSearchPanel } from "./GlobalSearchPanel";

type Props = {
  locale: string;
  storeSlug: string;
};

/** Coordinates for the fixed-position portal panel */
type PanelRect = {
  top: number;
  /** inline-start edge (left in LTR, right in RTL) */
  insetInlineEnd: number;
  width: number;
};

export function GlobalSearch({ locale, storeSlug }: Props) {
  const t = useTranslations();
  const panelId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [panelRect, setPanelRect] = useState<PanelRect | null>(null);
  /** Index of the keyboard-highlighted result row (-1 = none) */
  const [activeIndex, setActiveIndex] = useState(-1);

  const { data, isFetching, error } = useGlobalSearchQuery({
    storeSlug,
    q: value,
  });

  const trimmed = value.trim();
  const hasEnoughChars = trimmed.length >= 2;
  const showPanel = isOpen && hasEnoughChars;

  // Compute panel position from the container's bounding rect.
  // Called synchronously from event handlers (open, scroll, resize)
  // so setState is never called directly inside an effect body.
  const computePanelRect = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dir = document.documentElement.dir ?? "ltr";
    const isRtl = dir === "rtl";
    setPanelRect({
      top: rect.bottom + 8, // 8px gap below the header bar
      // In LTR we pin the panel's right edge to the container's right edge;
      // in RTL we pin the panel's left edge to the container's left edge.
      // We pass the distance from the relevant viewport edge so the panel
      // aligns naturally in both directions.
      insetInlineEnd: isRtl
        ? rect.left
        : window.innerWidth - rect.right,
      width: 320, // fixed 320px / w-80
    });
  }, []); // containerRef and setPanelRect are stable; no deps needed

  const open = useCallback(() => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      // Compute panel rect now that the container is definitely laid out
      computePanelRect();
    });
  }, [computePanelRect]);

  // ⌘K / Ctrl+K global hotkey: open & focus the search input.
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        open();
      }
    };
    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, [open]);

  // Attach scroll/resize listeners while the panel is open so the fixed
  // position tracks the header if the page scrolls or the window resizes.
  // setState is only called from the event callbacks, not from inside the
  // effect body itself, so this does not trigger cascading renders.
  useEffect(() => {
    if (!showPanel) return;
    window.addEventListener("scroll", computePanelRect, { capture: true, passive: true });
    window.addEventListener("resize", computePanelRect, { passive: true });
    return () => {
      window.removeEventListener("scroll", computePanelRect, { capture: true });
      window.removeEventListener("resize", computePanelRect);
    };
  }, [showPanel, computePanelRect]);

  // Close the panel when the user clicks outside the container.
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The results panel is portaled to document.body, so it lives OUTSIDE
      // containerRef. Without this exclusion, a mousedown on a result row is
      // treated as an outside click and closes (unmounts) the panel before the
      // click can navigate — i.e. clicking a result does nothing.
      const panelEl = document.getElementById(panelId);
      if (panelEl?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, panelId]);

  // Note: activeIndex is reset to -1 inside setValue calls (see onChange below)
  // so selection always restarts when the query changes — no separate effect needed.

  const close = () => {
    setIsOpen(false);
    setValue("");
    setPanelRect(null);
    setActiveIndex(-1);
  };

  const handleNavigate = () => {
    // Close the dropdown after a result is clicked
    setIsOpen(false);
    setValue("");
    setActiveIndex(-1);
  };

  // Build the flat list of navigable row IDs from the current result set so
  // ArrowUp/Down/Enter can index into them deterministically.
  const rowIds = buildRowIds(panelId, data);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (value === "") {
        setIsOpen(false);
        setActiveIndex(-1);
      } else {
        setValue("");
        setActiveIndex(-1);
      }
      return;
    }

    if (!showPanel || rowIds.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % rowIds.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev <= 0 ? rowIds.length - 1 : prev - 1,
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      // Programmatically click the active row link
      const el = document.getElementById(rowIds[activeIndex]);
      if (el) el.click();
    }
  };

  const activeRowId = activeIndex >= 0 ? rowIds[activeIndex] : undefined;

  return (
    <div ref={containerRef} className="relative flex items-center">
      {/* Search icon button — collapses/expands the input */}
      <button
        type="button"
        onClick={isOpen ? () => inputRef.current?.focus() : open}
        aria-label={t(keys.common.search)}
        aria-expanded={isOpen}
        aria-controls={showPanel ? panelId : undefined}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
      >
        <SearchIcon />
      </button>

      {/* Expanding input */}
      <input
        ref={inputRef}
        id={`${panelId}-input`}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls={showPanel ? panelId : undefined}
        aria-activedescendant={activeRowId}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          // Reset keyboard selection on every keystroke so Arrow navigation
          // always starts fresh when the query changes.
          setActiveIndex(-1);
          if (!isOpen) setIsOpen(true);
          // Recompute panel anchor whenever content changes; harmless no-op
          // if the panel is not yet visible (rect will be recomputed on open).
          computePanelRect();
        }}
        onFocus={() => {
          if (!isOpen) setIsOpen(true);
          computePanelRect();
        }}
        onKeyDown={handleKeyDown}
        placeholder={t(keys.globalSearch.placeholder)}
        aria-label={t(keys.globalSearch.placeholder)}
        type="search"
        autoComplete="off"
        className={[
          "ms-1 h-8 rounded-full border bg-[color:var(--surface)] text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none transition-[width,opacity,padding] duration-200",
          isOpen
            ? "w-[240px] border-[color:var(--border-light)] px-3 opacity-100"
            : "pointer-events-none w-0 border-transparent px-0 opacity-0",
        ].join(" ")}
      />

      {/* Clear button */}
      {isOpen && value ? (
        <button
          type="button"
          onClick={close}
          aria-label={t(keys.common.close)}
          className="absolute end-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-[color:var(--text-muted)] hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
        >
          <CloseIcon />
        </button>
      ) : null}

      {/* Results dropdown — rendered via portal to escape overflow:hidden header */}
      {showPanel && panelRect
        ? createPortal(
            <div
              id={panelId}
              role="listbox"
              aria-label={t(keys.globalSearch.placeholder)}
              style={{
                position: "fixed",
                top: panelRect.top,
                // Logical inset: right in LTR, left in RTL
                ...(document.documentElement.dir === "rtl"
                  ? { left: panelRect.insetInlineEnd }
                  : { right: panelRect.insetInlineEnd }),
                width: panelRect.width,
                zIndex: 9999,
              }}
              className="overflow-hidden rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)]"
            >
              {/* Loading: show skeleton while fetching; keep stale data visible
                  if available so the panel doesn't flash on re-queries. */}
              {isFetching && !data ? (
                <SearchLoadingSkeleton label={t(keys.globalSearch.loading)} />
              ) : error ? (
                <div
                  role="alert"
                  className="px-4 py-4 text-center text-[13px] text-[color:var(--error-500)]"
                >
                  {t(keys.globalSearch.error)}
                </div>
              ) : data ? (
                <GlobalSearchPanel
                  result={data}
                  locale={locale}
                  storeSlug={storeSlug}
                  q={trimmed}
                  onNavigate={handleNavigate}
                  activeRowId={activeRowId}
                  panelId={panelId}
                />
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function SearchLoadingSkeleton({ label }: { label: string }) {
  return (
    <div aria-label={label} aria-busy="true">
      {/* Visually hidden accessible label */}
      <span className="sr-only">{label}</span>
      {/* Three shimmer rows */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="h-4 w-4 shrink-0 animate-pulse rounded-full bg-[color:var(--background-muted)]" />
          <div className="flex flex-1 flex-col gap-1.5">
            <div
              className="h-3 animate-pulse rounded bg-[color:var(--background-muted)]"
              style={{ width: `${60 + i * 10}%` }}
            />
            <div
              className="h-2.5 animate-pulse rounded bg-[color:var(--background-muted)]"
              style={{ width: `${35 + i * 8}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build a flat ordered list of DOM IDs for the keyboard-navigable result rows.
 * Mirrors the exact render order in GlobalSearchPanel so Arrow navigation is
 * consistent with what the user sees.
 */
function buildRowIds(
  panelId: string,
  data: ReturnType<typeof useGlobalSearchQuery>["data"],
): string[] {
  if (!data) return [];
  if (data.kind === "orders") {
    return data.orders.map((o) => `${panelId}-row-order-${o.id}`);
  }
  if (data.kind === "customers") {
    // Navigate into the customer card links only (the card header link + inline
    // order links). "See all orders" footer link is intentionally excluded so
    // Enter drives the user to a specific entity rather than the list page.
    return data.customers.flatMap((c) => [
      `${panelId}-row-customer-${c.id}`,
      ...c.recentOrders.map((o) => `${panelId}-row-order-${o.id}`),
    ]);
  }
  if (data.kind === "mixed") {
    // Same order/exclusions as the "customers" branch above, followed by
    // the product rows — matches the render order in GlobalSearchPanel.
    const customerRowIds = data.customers.flatMap((c) => [
      `${panelId}-row-customer-${c.id}`,
      ...c.recentOrders.map((o) => `${panelId}-row-order-${o.id}`),
    ]);
    const productRowIds = data.products.map(
      (p) => `${panelId}-row-product-${p.id}`,
    );
    return [...customerRowIds, ...productRowIds];
  }
  return [];
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}
